import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import moment from "moment";
import ConfettiExplosion from "react-confetti-explosion";
import LotteryBall from "./LotteryBall";
import { Button, TextField, Box, FormControlLabel, Checkbox, Typography, Grid, Card } from "@mui/material";
import { Download, ColorLens, CalendarToday, Image } from "@mui/icons-material";

const Lottery = () => {
  const [numbers, setNumbers] = useState(["", "", "", "", ""]);
  const [selected, setSelected] = useState([false, false, false, false, false]);
  const [color, setColor] = useState("#ff416c");
  const [bgColor, setBgColor] = useState("#f0f4f8");
  const [textColor, setTextColor] = useState("#ffffff");
  const [note, setNote] = useState("");
  const [noteColor, setNoteColor] = useState("#555555");
  const [showDate, setShowDate] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [logo, setLogo] = useState(null);
  const lotteryRef = useRef(null);

  const handleNumberChange = (index, value) => {
    if (value.length <= 2 && /^\d*$/.test(value)) {
      const newNumbers = [...numbers];
      newNumbers[index] = value;
      setNumbers(newNumbers);
    }
  };

  const handleCheckChange = (index) => {
    const newSelected = [...selected];
    newSelected[index] = !newSelected[index];
    setSelected(newSelected);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogo(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateImage = () => {
    if (numbers.some(num => num.length !== 2)) {
      alert("Completa todos los números con exactamente 2 dígitos antes de generar la imagen.");
      return;
    }
    setShowConfetti(true);
    setTimeout(() => {
      html2canvas(lotteryRef.current, { scale: 2, backgroundColor: null }).then((canvas) => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = "lottery-numbers.png";
        link.click();
        setShowConfetti(false);
      });
    }, 1000);
  };

  return (
    <Box sx={{ padding: { xs: 2, md: 4 }, textAlign: "center", bgcolor: "#fafcff", minHeight: "100vh" }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold", color: "#333" }}>
        🎉 ¡Tu combinación de suerte! 🎉
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        {numbers.map((num, index) => (
          <Grid item key={index} xs={6} sm={4} md={2} sx={{ textAlign: "center" }}>
            <TextField
              type="number"
              inputProps={{ maxLength: 2, max: 99, min: 0 }}
              value={num}
              onChange={(e) => handleNumberChange(index, e.target.value.slice(0, 2))}
              label={`Número ${index + 1}`}
              variant="outlined"
              fullWidth
              sx={{ bgcolor: "white", borderRadius: 2 }}
            />
            <FormControlLabel
              control={<Checkbox checked={selected[index]} onChange={() => handleCheckChange(index)} />}
              label="Destacar"
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ my: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
          <ColorLens color="primary" />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
          <Typography>Color de bolos</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
          <ColorLens color="secondary" />
          <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
          <Typography>Color del fondo</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
          <ColorLens />
          <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
          <Typography>Color números</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
          <ColorLens />
          <input type="color" value={noteColor} onChange={(e) => setNoteColor(e.target.value)} />
          <Typography>Color Nota</Typography>
        </Box>
      </Box>

      <Button variant="outlined" component="label" startIcon={<Image />} sx={{ mb: 2 }}>
        Subir Logo
        <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
      </Button>

      <TextField
        label="📝 Añadir una nota"
        variant="outlined"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ width: "100%", maxWidth: 400, mb: 2, bgcolor: "white", borderRadius: 2 }}
      />

      <FormControlLabel
        control={<Checkbox checked={showDate} onChange={() => setShowDate(!showDate)} />}
        label={<Typography><CalendarToday sx={{ fontSize: 18 }} /> Mostrar fecha</Typography>}
      />

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" color="primary" startIcon={<Download />} onClick={generateImage} size="large">
          Descargar Imagen
        </Button>
      </Box>

      {showConfetti && <ConfettiExplosion />}

      <Card ref={lotteryRef} sx={{ width: "100%", maxWidth: 550, mx: "auto", my: 4, p: 4, bgcolor: bgColor, borderRadius: 4, boxShadow: 5, border: "5px solid #333", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        {logo && <img src={logo} alt="Logo" style={{ maxWidth: 100, marginBottom: 20 }} />}
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#444" }}>
          🍀 Números de la suerte 🍀
        </Typography>

        {showDate && (<Typography sx={{ mt: 1, color: "#666", fontStyle: "italic" }}>📅 {moment().format("DD [de] MMMM YYYY")}</Typography>)}

        <Grid container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
          {numbers.map((num, index) => (
            <Grid item key={index}>
              <LotteryBall number={num} isHighlighted={selected[index]} color={color} textColor={textColor} fireEffect={selected[index]} />
            </Grid>
          ))}
        </Grid>

        {note && <Typography sx={{ mt: 2, fontStyle: "italic", color: noteColor }}>"{note}"</Typography>}
      </Card>
    </Box>
  );
};

export default Lottery;
